/**
 * The safe-display contract for saved connection records (security slice, 2026-09-28; `Docs/ConnectionSecrets.md`).
 *
 * A saved `server`, an Oracle `oracleTnsDescriptor` or an alias built from them can hold a secret: a pasted connection
 * string (`Password=…`, `AccountKey=…`, `AppKey=…`), a SAS URL (`sig=…`), URL userinfo, or a descriptor's
 * `(SECURITY=(MY_WALLET_PASSWORD=…))`. Stored records are never rewritten; every surface that shows, logs, exports or
 * hands a record to an agent goes through these helpers instead of reading `alias || server`. Shared by the renderer and
 * main (both import `src/types`).
 *
 * - `safeConnectionName` — alias, else the safe server text, else a generic engine label; never raw server text.
 * - `safeConnectionServer` — the server as the app shows it where it used to show the raw server (hosts, tooltips).
 * - `safeConnectionTarget` — where it dials, as one line (`describeTarget`), secrets masked.
 * - `safeServerText` — the same rules for a bare string already copied out of a record (tab state, session files).
 * - `redactSecretText` — error / log / crash text with every embedded secret masked (`***`).
 */
import { describeTarget } from './connectionTransfer';
import { replaceJwts } from './jwtScanner';
import { describeOracleDescriptor, redactTargetSecrets } from './targetSecrets';

/** The record fields the helpers read — a `Connection`, an MCP `ConnectionData` and an `ExportedConnection` all fit. */
export type SafeDisplayRecord = {
  engine?: string;
  alias?: string;
  /** Some main-process shapes carry a `name`; treated like the alias. */
  name?: string;
  server?: string;
  port?: string | number;
  filePath?: string;
  oracleConnectionStringType?: string;
  oracleTnsAlias?: string;
  oracleTnsAdmin?: string;
  oracleTnsDescriptor?: string;
};

const ENGINE_LABELS: Record<string, string> = {
  mssql: 'SQL Server',
  pgsql: 'PostgreSQL',
  mysql: 'MySQL',
  oracle: 'Oracle',
  sqlite: 'SQLite',
  kusto: 'Azure Data Explorer',
  azurestorage: 'Azure Storage',
  mock: 'Mock',
};

/** The engine's display name ("SQL Server", "Oracle", …); a record without an engine is SQL Server (the app default). */
export function engineDisplayLabel(engine?: string): string {
  return ENGINE_LABELS[engine || 'mssql'] ?? 'Database';
}

// JWTs are found by `replaceJwts` (`jwtScanner.ts`): after any character, in linear time (SBT-06; round 3, Fable F4).
const AUTHORIZATION = /\b(Authorization\s*[:=]\s*)((?:Bearer|Basic|Negotiate|NTLM|Digest)\s+)?[^\s,;'"]+/giu;
const BEARER = /\b(Bearer)\s+[A-Za-z0-9\-._~+/]{16,}=*/gu;

/**
 * Text with every embedded secret masked as `***`: secret-named keys (quote-aware, fails closed — `redactTargetSecrets`),
 * URL userinfo passwords, `Authorization:` values, bearer tokens and JWTs. For errors, logs, crash reports and tool
 * output; idempotent.
 */
export function redactSecretText(text: string): string {
  if (!text) return text;
  return replaceJwts(redactTargetSecrets(text), '***')
    .replace(AUTHORIZATION, (_match, prefix: string, scheme?: string) => `${prefix}${scheme ?? ''}***`)
    .replace(BEARER, '$1 ***');
}

/** A leading `(KEY=` — possibly an Oracle descriptor; not SQL Server's `(localdb)\…`. */
const DESCRIPTOR_START = /^\(\s*[A-Za-z_][\w.-]*\s*=/u;
const DESCRIPTION_START = /^\(\s*DESCRIPTION(?:_LIST)?\s*=/iu;

function describedDescriptor(descriptor: string): string {
  const described = describeOracleDescriptor(descriptor);
  return described === 'TNS descriptor' ? described : `${described} (TNS descriptor)`;
}

/**
 * A bare server / name string as it may be shown: an Oracle descriptor as its host / port / service (never the
 * descriptor), anything else with its secrets masked. An Oracle server that starts with `(` is always treated as a
 * descriptor (unreadable → "TNS descriptor"). Without that engine, text is described only when it IS one — it parses,
 * or it starts `(DESCRIPTION` — so SQL Server's `(localdb)\MSSQLLocalDB` or an alias like `(env=prod) Orders` stays.
 */
export function safeServerText(text: string | undefined | null, engine?: string): string {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return '';
  if (engine === 'oracle' && trimmed.startsWith('(')) return describedDescriptor(trimmed);
  if (DESCRIPTOR_START.test(trimmed)) {
    const described = describedDescriptor(trimmed);
    if (described !== 'TNS descriptor' || DESCRIPTION_START.test(trimmed)) return described;
  }
  return redactSecretText(trimmed);
}

/**
 * The record's server as the app shows it where it used to show the raw `server` (a host suffix, a tooltip, a tab's
 * `serverHost`): SQLite its file; an Oracle TNS record without a server its alias or described descriptor.
 */
export function safeConnectionServer(record: SafeDisplayRecord | undefined | null): string {
  if (!record) return '';
  if (record.engine === 'sqlite') return safeServerText(record.filePath || record.server, 'sqlite');
  const server = safeServerText(record.server, record.engine);
  if (server || record.engine !== 'oracle') return server;
  if (record.oracleConnectionStringType === 'tns_alias') return safeServerText(record.oracleTnsAlias);
  return record.oracleTnsDescriptor ? describedDescriptor(record.oracleTnsDescriptor) : '';
}

/** Where the record dials, as one line (`describeTarget`: `server:port`, the SQLite file, the TNS alias or the described descriptor), secrets masked. */
export function safeConnectionTarget(record: SafeDisplayRecord | undefined | null): string {
  if (!record) return '';
  if (record.engine === 'sqlite') return safeServerText(record.filePath || record.server, 'sqlite');
  return redactSecretText(describeTarget({ ...record, engine: record.engine ?? '' }));
}

/**
 * The alias alone (a descriptor-shaped one described, any secret masked), or `fallback` when the record has none — for
 * agent payloads (MCP `connections_list`, the context snapshot) that keep `alias` and `server` as separate fields.
 */
export function safeConnectionAlias(record: SafeDisplayRecord | undefined | null, fallback = 'Unnamed'): string {
  const alias = String(record?.alias ?? '').trim() || String(record?.name ?? '').trim();
  return alias ? safeServerText(alias) : fallback;
}

/**
 * The name a connection is shown, logged and handed to agents under: its alias (a descriptor-shaped alias described,
 * any secret in it masked — imports and older builds could copy a server into the alias), else the safe server text,
 * else "<Engine> connection". Never raw server text; never empty for a record.
 */
export function safeConnectionName(record: SafeDisplayRecord | undefined | null, fallback = 'Connection'): string {
  if (!record) return fallback;
  const alias = String(record.alias ?? '').trim() || String(record.name ?? '').trim();
  if (alias) return safeServerText(alias);
  const server = safeConnectionServer(record);
  if (server && server !== 'TNS descriptor') return server;
  return `${engineDisplayLabel(record.engine)} connection`;
}
